/**
 * One Main Command visual timeline (#383).
 *
 * Visual merge only: Command/planner writes and Observer discussion writes stay
 * on their own authorities. Rendering Owner + Overseer + Observer UX + Observer
 * Core together must never imply that discussion is executable work.
 */
import type { CommandTurn } from './command-chat';
import type { ChatMessage } from './data';
import type { ObserverMessageRow, ObserverThreadRow } from './observer-view';
import {
  roadmapReadQuestion,
  type ManagementCommunicationPreferences,
} from '@brunel/shared';
import {
  executionIdentityByline,
  targetBadges,
  type MainCommandDirectRecipient,
  type MainCommandReplyTarget,
} from './main-command-recipients';
import type { MainCommandRecipientStatus } from './main-command-delivery';

export type SharedTimelineRole =
  | 'owner'
  | 'overseer'
  | 'observer_ux'
  | 'observer_core'
  | 'observer_system'
  | 'worker';

/** Which write path produced (or would produce) this turn. */
export type SharedTimelineAuthority = 'command' | 'observer_discussion' | 'worker_chat';

export type CommandDiscussionMode =
  | { readonly kind: 'command' }
  | {
    readonly kind: 'discussion';
    readonly threadId: string | null;
    readonly title: string;
    readonly source: 'observer';
  };

export interface SharedTimelineItem {
  readonly id: string;
  readonly role: SharedTimelineRole;
  readonly authority: SharedTimelineAuthority;
  readonly mine: boolean;
  readonly text: string;
  readonly atLabel: string;
  /** Stable order key; lower sorts earlier. */
  readonly sortKey: number;
  readonly byline: string;
  readonly statusLabel: string | null;
  readonly reason: string | null;
  readonly offer: CommandTurn['offer'];
  readonly outcome: CommandTurn['outcome'];
  readonly threadId: string | null;
  readonly commandTurn: CommandTurn | null;
  readonly targetRoles: readonly MainCommandReplyTarget[];
  readonly targetWorkerLabels: readonly string[];
  /** A reply shortcut for a real participant byline. Owner/system rows have none. */
  readonly replyRecipient?: MainCommandDirectRecipient | null;
  /** Per-recipient delivery/reply truth for owner turns (#550). */
  readonly recipientStatuses?: readonly MainCommandRecipientStatus[];
  /**
   * Absolute time for archive boundaries (#415). Observer messages carry
   * created_at; command-authority turns use seq groups instead (null here).
   */
  readonly occurredAtMs: number | null;
  /**
   * #651: when this message was sent, exactly as its own record stores it -
   * owner_commands / plans / observer_messages / chat_messages created_at. Null or
   * absent when the record has none. Shown only; never ordering or archive range.
   */
  readonly sentAt?: string | null;
}

export const SHARED_ROLE_LABEL: Readonly<Record<SharedTimelineRole, string>> = {
  owner: 'You',
  overseer: 'Overseer',
  observer_ux: 'Observer UX',
  observer_core: 'Observer Core',
  observer_system: 'Observer',
  worker: 'Worker',
};

/** #709: who a Roadmap Autopilot-derived command is shown as. */
export const ROADMAP_AUTOPILOT_BYLINE = 'Roadmap Autopilot · from a roadmap item';

export function commandDiscussionMode(): CommandDiscussionMode {
  return { kind: 'command' };
}

export function discussionModeForThread(
  thread: Pick<ObserverThreadRow, 'id' | 'title'>,
): CommandDiscussionMode {
  return {
    kind: 'discussion',
    threadId: thread.id,
    title: thread.title,
    source: 'observer',
  };
}

export function isDiscussionMode(mode: CommandDiscussionMode): mode is Extract<CommandDiscussionMode, { kind: 'discussion' }> {
  return mode.kind === 'discussion';
}

/**
 * Who wrote a stored message. Observer UX and Observer Core no longer write, and what they
 * wrote keeps their names. The Reviewer writes no message, so it has no role here.
 */
export function observerRoleFromAuthor(authorRole: ObserverMessageRow['author_role']): SharedTimelineRole {
  switch (authorRole) {
    case 'owner': return 'owner';
    case 'overseer': return 'overseer';
    case 'ux': return 'observer_ux';
    case 'core': return 'observer_core';
    // System notes, and any author this build does not know, are never shown as a participant.
    default: return 'observer_system';
  }
}

export function sharedByline(role: SharedTimelineRole, atLabel: string, seq: number | null = null): string {
  const base = role === 'owner' && seq !== null ? `${SHARED_ROLE_LABEL.owner} · #${seq}` : SHARED_ROLE_LABEL[role];
  if (role === 'owner') return base;
  return atLabel === '' ? base : `${base} · ${atLabel}`;
}

export function mainCommandUnreadCount(threads: readonly ObserverThreadRow[]): number {
  return threads
    .filter((thread) => thread.status === 'open' || thread.status === 'approved' || thread.status === 'prototype')
    .reduce((sum, thread) => sum + thread.unread_count, 0);
}

/** Prefer the most recent open Observer finding for unread/discussion navigation. */
export function preferOpenDiscussionThread(
  threads: readonly ObserverThreadRow[],
): ObserverThreadRow | null {
  const open = threads.filter((thread) =>
    thread.status === 'open' || thread.status === 'approved' || thread.status === 'prototype');
  if (open.length === 0) return null;
  return [...open].sort((a, b) => (Date.parse(b.updated_at) || 0) - (Date.parse(a.updated_at) || 0))[0] ?? null;
}

function executionCommandId(identity: unknown): string | null {
  if (identity === null || typeof identity !== 'object' || Array.isArray(identity)) return null;
  const value = (identity as Record<string, unknown>)['commandId'];
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * The Overseer heartbeat's own note: one per round that changed something. It is the one
 * unsolicited Overseer message Main Command shows, because the owner asked to hear it there.
 */
export function isHeartbeatNote(message: ObserverMessageRow): boolean {
  const identity = message.execution_identity;
  if (message.author_role !== 'overseer' || identity === null || typeof identity !== 'object' || Array.isArray(identity)) return false;
  return (identity as Record<string, unknown>)['source'] === 'autopilot_heartbeat';
}

export function buildSharedCommandTimeline(input: {
  readonly commandTurns: readonly CommandTurn[];
  readonly observerMessages: readonly ObserverMessageRow[];
  readonly workerMessages?: readonly ChatMessage[];
  readonly workerLabels?: Readonly<Record<string, string>>;
  readonly discussion: CommandDiscussionMode;
  readonly openThreadIds?: ReadonlySet<string>;
  readonly nowMs?: number;
  readonly communication?: ManagementCommunicationPreferences;
  readonly recipientStatusesByCommand?: ReadonlyMap<string, readonly MainCommandRecipientStatus[]>;
}): readonly SharedTimelineItem[] {
  const items: SharedTimelineItem[] = [];
  const discussion = input.discussion;

  input.commandTurns.forEach((turn, index) => {
    // #709: a command Roadmap Autopilot wrote is a system turn with its own byline, never "You".
    const generated = turn.generatedBy === 'roadmap_autopilot';
    const role: SharedTimelineRole = generated ? 'observer_system' : turn.mine ? 'owner' : 'overseer';
    items.push({
      id: `command:${turn.id}`,
      role,
      authority: 'command',
      mine: turn.mine,
      text: turn.text,
      atLabel: turn.atLabel,
      sortKey: index,
      // #651: the time is its own element beside the byline, from sentAt.
      byline: generated
        ? `${ROADMAP_AUTOPILOT_BYLINE}${turn.seq === null ? '' : ` · #${turn.seq}`}`
        : sharedByline(role, '', turn.seq),
      statusLabel: turn.statusLabel,
      reason: turn.reason,
      offer: turn.offer,
      outcome: turn.outcome,
      threadId: null,
      commandTurn: turn,
      targetRoles: turn.mine ? targetBadges(turn.replyTargets) : [],
      targetWorkerLabels: turn.mine
        ? (turn.workerTargets ?? []).map((workerId) => input.workerLabels?.[workerId] ?? 'Worker')
        : [],
      replyRecipient: turn.mine || generated ? null : { kind: 'management', target: 'overseer' },
      recipientStatuses: turn.mine ? input.recipientStatusesByCommand?.get(turn.id) ?? [] : [],
      occurredAtMs: null,
      sentAt: turn.sentAt ?? null,
    });
  });

  const commandCount = input.commandTurns.length;
  const canonicalRoadmapCommands = new Set(input.commandTurns
    .filter((turn) => turn.mine && roadmapReadQuestion(turn.text) !== null)
    .map((turn) => turn.id));
  const allowedThreadId = isDiscussionMode(discussion) ? discussion.threadId : null;
  const anchorBefore = uncorrelatedAnchors(input.commandTurns, input.observerMessages, input.workerMessages ?? []);
  for (const message of input.observerMessages) {
    const commandId = executionCommandId(message.execution_identity);
    if (message.author_role === 'overseer' && commandId !== null
      && canonicalRoadmapCommands.has(commandId) && !isDiscussionMode(discussion)) continue;
    const directlyRequested = commandId !== null;
    const noteLabel = isHeartbeatNote(message) ? 'Autopilot heartbeat' : null;
    const heartbeat = noteLabel !== null;
    if (allowedThreadId !== null && message.thread_id !== allowedThreadId) continue;
    if (allowedThreadId === null && input.openThreadIds && !input.openThreadIds.has(message.thread_id) && !directlyRequested && !heartbeat) continue;
    const discussionContribution = message.discussion_id != null;
    if (isDiscussionMode(discussion) && message.kind === 'conclusion') continue;
    // Observer discussions were removed; what they wrote earlier is not part of the conversation.
    if (!isDiscussionMode(discussion) && discussionContribution) continue;
    const role = observerRoleFromAuthor(message.author_role);
    const atMs = Date.parse(message.created_at);
    if (!isDiscussionMode(discussion)) {
      // Main Command is the owner's conversation, not an Observer activity feed.
      // Unsolicited reviews, findings, recurrence updates and owner-gated
      // conclusions remain durable in Findings / Attention / Cases. Only a reply
      // correlated to an owner command belongs in the ordinary chat timeline.
      if ((!directlyRequested && !heartbeat) || (role === 'observer_system' && !heartbeat)) continue;
    }
    const ownerIndex = commandId === null ? -1 : input.commandTurns.findIndex((turn) => turn.mine && turn.id === commandId);
    // A role reply sits with the owner message that requested it. Independent
    // UX/Core completion order remains timestamp-stable, while later owner
    // messages never leap ahead of a delayed reply to an earlier command.
    // Uncorrelated Observer messages that are provably older than an owner
    // turn's replies sit before that turn, so a backlog of finding reviews
    // cannot bury the newest direct reply; newer ones stay at the end.
    const anchor = ownerIndex < 0 && Number.isFinite(atMs) ? anchorBefore(atMs) : null;
    const sortKey = ownerIndex >= 0
      ? ownerIndex + 0.5 + (Number.isFinite(atMs) ? (atMs % 1_000_000) / 1_000_000_000 : 0)
      : anchor !== null
        ? anchor - 0.3
        : commandCount + (Number.isFinite(atMs) ? atMs / 1e13 : 0);
    const identity = executionIdentityByline(message.execution_identity);
    const observerByline = role === 'owner' ? 'Owner' : sharedByline(role, '');
    items.push({
      id: `observer:${message.id}`,
      role,
      authority: 'observer_discussion',
      mine: role === 'owner',
      text: message.body,
      atLabel: '',
      sortKey,
      byline: noteLabel !== null ? `${observerByline} · ${noteLabel}` : identity === null ? observerByline : `${observerByline} · ${identity}`,
      statusLabel: message.kind === 'review' || message.kind === 'reply' ? null : message.kind,
      reason: null,
      offer: null,
      outcome: null,
      threadId: message.thread_id,
      commandTurn: null,
      targetRoles: [],
      targetWorkerLabels: [],
      // An Observer's past message keeps its byline and offers no reply shortcut: it no longer answers.
      replyRecipient: role === 'overseer' ? { kind: 'management', target: 'overseer' } : null,
      occurredAtMs: Number.isFinite(atMs) ? atMs : null,
      sentAt: Number.isFinite(atMs) ? message.created_at : null,
    });
  }

  for (const message of input.workerMessages ?? []) {
    if (message.author_kind !== 'worker' || !message.owner_command_id) continue;
    const ownerIndex = input.commandTurns.findIndex((turn) => turn.mine && turn.id === message.owner_command_id);
    if (ownerIndex < 0) continue;
    const atMs = Date.parse(message.created_at);
    const identity = message.author_identity ?? {};
    const workerNumber = typeof identity['workerNumber'] === 'number' ? identity['workerNumber'] : null;
    const workerName = typeof identity['workerName'] === 'string' ? identity['workerName'].trim() : '';
    const workerLabel = workerNumber === null
      ? 'Worker'
      : workerName === '' || workerName === `Worker ${workerNumber}`
        ? `Worker ${workerNumber}`
        : `Worker ${workerNumber} · ${workerName}`;
    const node = typeof identity['nodeName'] === 'string' && identity['nodeName'].trim() !== ''
      ? identity['nodeName'].trim() : null;
    const runtime = typeof identity['runtimeKey'] === 'string' && identity['runtimeKey'].trim() !== ''
      ? identity['runtimeKey'].trim() : null;
    const model = typeof identity['modelKey'] === 'string' && identity['modelKey'].trim() !== ''
      ? identity['modelKey'].trim() : null;
    const route = [node, runtime === null ? null : model === null ? runtime : `${runtime} / ${model}`]
      .filter((part): part is string => part !== null)
      .join(' · ');
    const sortKey = ownerIndex + 0.6
      + (Number.isFinite(atMs) ? (atMs % 1_000_000) / 1_000_000_000 : 0);
    items.push({
      id: `worker:${message.id}`,
      role: 'worker',
      authority: 'worker_chat',
      mine: false,
      text: message.content,
      atLabel: '',
      sortKey,
      byline: route === '' ? workerLabel : `${workerLabel} · ${route}`,
      statusLabel: null,
      reason: null,
      offer: null,
      outcome: null,
      threadId: null,
      commandTurn: null,
      targetRoles: [],
      targetWorkerLabels: [],
      replyRecipient: { kind: 'worker', workerId: message.worker_id! },
      occurredAtMs: Number.isFinite(atMs) ? atMs : null,
      sentAt: Number.isFinite(atMs) ? message.created_at : null,
    });
  }

  return items.sort((a, b) => {
    if (a.sortKey !== b.sortKey) return a.sortKey - b.sortKey;
    if (a.occurredAtMs !== null && b.occurredAtMs !== null && a.occurredAtMs !== b.occurredAtMs) {
      return a.occurredAtMs - b.occurredAtMs;
    }
    return a.id.localeCompare(b.id);
  });
}

/**
 * Owner commands carry no timestamp, but every reply correlated to one proves
 * the command was sent before that reply. The earliest correlated reply at or
 * after each owner turn is therefore an upper bound on when it was sent. The
 * returned lookup names the first owner turn (timeline index) whose bound is
 * later than a message, or null when no known reply is later (tail placement).
 */
function uncorrelatedAnchors(
  turns: readonly CommandTurn[],
  observerMessages: readonly ObserverMessageRow[],
  workerMessages: readonly ChatMessage[],
): (atMs: number) => number | null {
  const firstReplyAt = new Map<string, number>();
  const note = (commandId: string | null | undefined, createdAt: string) => {
    const atMs = Date.parse(createdAt);
    if (!commandId || !Number.isFinite(atMs)) return;
    const known = firstReplyAt.get(commandId);
    if (known === undefined || atMs < known) firstReplyAt.set(commandId, atMs);
  };
  for (const message of observerMessages) note(executionCommandId(message.execution_identity), message.created_at);
  for (const message of workerMessages) {
    if (message.author_kind === 'worker') note(message.owner_command_id, message.created_at);
  }
  const bounds: { readonly index: number; readonly sentBeforeMs: number }[] = [];
  let bound = Number.POSITIVE_INFINITY;
  for (let index = turns.length - 1; index >= 0; index -= 1) {
    const turn = turns[index]!;
    if (!turn.mine) continue;
    bound = Math.min(bound, firstReplyAt.get(turn.id) ?? Number.POSITIVE_INFINITY);
    if (Number.isFinite(bound)) bounds.unshift({ index, sentBeforeMs: bound });
  }
  return (atMs) => bounds.find((entry) => atMs < entry.sentBeforeMs)?.index ?? null;
}

export function discussionSendAuthority(mode: CommandDiscussionMode): SharedTimelineAuthority {
  return isDiscussionMode(mode) && mode.threadId !== null
    ? 'observer_discussion'
    : 'command';
}
